import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { MilkMoneyQuery } from '~/QueryHooks/MilkMoneyQuery'
import MilkMoneyAccountingService from '~/api/services/milkMoneyAccounting'
import Dialog from '~/components/Dialog'
import Layout from '~/components/Layout'
import { El } from '~/components/Layout/style'
import notify from '~/components/Notify'
import TableComponent from '~/components/TableComponent'
import i18n from '~/components/i18n'
import { useDebounce } from 'use-lodash-debounce';
import { RouteTypes } from '~/routes/constants'
import { Input } from 'semantic-ui-react'
import { LIGHT_USER_ROLE_SLUG } from '~/Constants'
import { useAuth } from '~/routes/components/AuthProvider'
import { handleErrors } from '~/utils/helpers'

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Article No.'),
    key: 'uniqueId',
    sorted: 'desc'
  },
  {
    name: i18n.t('Article name'),
    key: 'name',
    sorted: 'desc'
  },
  {
    name: i18n.t('Unit'),
    key: 'unitLabel'
  },
  {
    name: i18n.t('Period'),
    key: 'intervalLabel'
  },
  {
    name: i18n.t('Article price'),
    key: 'netPrice',
    type: 'no-unit-number',
    decimalScale: 5
  },
  {
    name: i18n.t('Tax'),
    key: 'saleTaxLabel'
  },
  {
    name: i18n.t('Article price bruto (in €)'),
    key: 'grossPrice',
    type: 'no-unit-number'
  },
  {
    name: i18n.t('Customizable'),
    key: 'custom',
    type: 'verified'
  },
  {
    name: i18n.t('Actions')
  }
]

const getActions = () => [
  {
    popupMessage: i18n.t('Edit'),
    type: 'link',
    name: 'pencil',
    size: 'large',
    link: RouteTypes.DAIRY_FACTORY_PROFILE_SHOP_EDIT
  },
  {
    popupMessage: i18n.t('Delete'),
    type: 'icon',
    name: 'trash',
    size: 'large',
    actionType: 'delete',
    verifiedActionKey: 'deleteAvailable',
    verifiedAction: 1
  }
]

const ShopManagment: React.FC = () => {
  const { t } = useTranslation()
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const lighUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG

  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const [deleteArticleData, setDeleteArticleData] = useState(null)
  const [loadingDeleteAction, setLoadingDeleteAction] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedValue = useDebounce(search, 300);
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 10,
    search: ''
  })
  const navigate = useNavigate()

  const {
    data: articleData,
    isLoading: isBusy,
    refetch: fetchData,
    isFetching: isFetchingData
  } = MilkMoneyQuery.getMilkMoneyArticles({ ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 })

  useEffect(() => {
    fetchData()
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
    fetchData()
  }, [t])

  function handleActionClick(row, type) {
    switch (type) {
      case 'delete':
        return setDeleteArticleData(row)
      default:
        return
    }
  }

  function renderTable() {
    return <TableComponent
      isFetchingData={isFetchingData}
      animated
      actions={lighUserLogged ? [] : getActions()}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      tableColumnHeaders={lighUserLogged ? tableColumnHeaders.filter(tableColumn => !!tableColumn.key) : tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setDataFiltered(data)
        setTableColumnHeaders(tableColumnHeaders)
      }}
      rows={rows}
      total={filtered}
    />
  }

  function renderActions() {
    return <El.PageHeaderActionButton
      size='large'
      onClick={() => navigate(RouteTypes.DAIRY_FACTORY_PROFILE_SHOP_CREATE)}
    >
      {t('Create article')}
    </El.PageHeaderActionButton>
  }

  async function handleDeleteArticle() {
    setLoadingDeleteAction(true)
    try {
      await MilkMoneyAccountingService.deleteMilkMoneyArticle(deleteArticleData?.id)
      setDeleteArticleData(null)
      notify(t('Successfully deleted article'), 'success')
      fetchData()
    } catch (error) {
      handleErrors(error)
    }
    setLoadingDeleteAction(false)
  }

  function renderDeleteModal() {
    const name = deleteArticleData?.name
    return <Dialog
      isDialogOpen={Boolean(deleteArticleData)}
      textHeader={t('Delete article {{name}}', { name })}
      textClose={t('Close')}
      handleClose={() => setDeleteArticleData(null)}
      textContent={t('Do you want to delete article {{name}}?', { name })}
      textSubmit={t('Delete')}
      type={'abort'}
      isLoading={loadingDeleteAction}
      disableSubmit={loadingDeleteAction}
      handleSubmit={() => handleDeleteArticle()}
    />
  }

  function renderFilters() {
    return <>
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    </>
  }

  const { filtered, rows } = articleData ? articleData : { filtered: 0, rows: [] };

  return <Layout
    title={t('Shop managment')}
    useSpinner
    childContainerSize={'300px'}
    fontSizeHeader={'18px'}
    actions={!lighUserLogged && renderActions()}
    isBusy={isBusy || isFetchingData}
    filters={renderFilters()}
  >
    {Boolean(deleteArticleData) && renderDeleteModal()}
    {renderTable()}
  </Layout>
}
export default ShopManagment